import { api } from './api.ts'
import { MIN_ASK_LENGTH, normalizeAskQuery } from './search-ask.ts'

/** Where an opened search result came from. */
export type SearchOpenedSource = 'jev' | 'keyword'

/** One opened result, as `POST /search-opened` takes it. */
export interface SearchOpened {
  /** The question the AI was asked, as the reader typed it. */
  query: string
  pageId: string
  /** The 1-based position among the results of its source. */
  rank: number
  source: SearchOpenedSource
}

const MAX_RANK = 100
const MAX_ASK_LENGTH = 500

/**
 * Record which result the reader opened after an AI ask.
 *
 * Fire and forget: it returns before the request settles, and a failure is
 * dropped, so navigation never waits on it. Call it only once an ask has
 * answered, because the backend writes onto that ask's record.
 */
export function recordSearchOpened(opened: SearchOpened): void {
  // The record is keyed by the normalized question, and the backend checks its length.
  const query = normalizeAskQuery(opened.query)
  if (query.length < MIN_ASK_LENGTH || query.length > MAX_ASK_LENGTH) return
  if (opened.rank < 1 || opened.rank > MAX_RANK) return
  try {
    // keepalive lets the request outlive a full page load.
    void api
      .post('search-opened', {
        json: { ...opened, query },
        retry: 0,
        keepalive: true
      })
      .catch(() => undefined)
  } catch {
    return
  }
}

/**
 * The 1-based position of a keyword match when results are ordered by score.
 *
 * @returns null for a page that does not match.
 */
export function keywordRank(
  scores: ReadonlyMap<string, number>,
  key: string
): number | null {
  const score = scores.get(key) ?? 0
  if (score <= 0) return null
  let rank = 1
  for (const other of scores.values()) if (other > score) rank += 1
  return rank
}
